import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, UploadCloud, AlertCircle, CheckCircle2, FileText, RotateCcw } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import api, { formatCurrency, formatDate, formatApiErrorDetail } from "../../lib/api";
import { useSmartBack } from "../../lib/backNavigation";
import { notifyInstallMilestone } from "../../lib/installMilestone";
import { toast } from "sonner";

const METHOD_OPTIONS = [
  { v: "venmo", label: "Venmo" },
  { v: "cashapp", label: "Cash App" },
  { v: "zelle", label: "Zelle" },
  { v: "cash", label: "Cash" },
  { v: "check", label: "Check" },
  { v: "other", label: "Other" },
];

export default function MarkAsPaid() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Contextual back — usually /parent/owed, but honors ?from=… when the
  // parent tapped a fee card on their dashboard or history.
  const back = useSmartBack("/parent/owed", "Fees to Pay");
  const [record, setRecord] = useState(null);
  const [method, setMethod] = useState("venmo");
  const [note, setNote] = useState("");
  const [receiptPath, setReceiptPath] = useState("");
  const [receiptPreview, setReceiptPreview] = useState(""); // local blob URL for image thumbnail
  const [receiptName, setReceiptName] = useState("");       // uploaded filename (for PDFs / accessibility)
  const [receiptIsImage, setReceiptIsImage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/payments")
      .then((r) => {
        const found = r.data.find((x) => x.id === id);
        setRecord(found || null);
        // Milestone — parent has viewed a fee.
        if (found) {
          notifyInstallMilestone();
          // Preserve previous payment method + note when the parent is
          // resubmitting after a rejection (or coming back to update an
          // in-flight submission). Falls back to defaults otherwise.
          if (found.method) setMethod(found.method);
          if (found.note) setNote(found.note);
          // Deliberately do NOT preload receipt_path — a rejected payment
          // most often needs a fresh proof of payment, and forcing the
          // parent to re-attach or explicitly reuse keeps the intent clear.
        }
      })
      .catch(() => setRecord(null));
  }, [id]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    // Build a local blob URL for image previews so the parent can see what
    // they just attached — no roundtrip needed for the thumbnail itself.
    const isImage = f.type.startsWith("image/");
    const preview = isImage ? URL.createObjectURL(f) : "";
    try {
      const data = new FormData();
      data.append("file", f);
      const r = await api.post("/files/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      setReceiptPath(r.data.path);
      setReceiptName(f.name);
      setReceiptIsImage(isImage);
      // Only surface the preview URL after the upload succeeds, so a failed
      // upload never leaves a "green checkmark" showing.
      setReceiptPreview(preview);
      toast.success("Receipt uploaded successfully", {
        description: "Tap Replace if you attached the wrong file.",
      });
    } catch (e) {
      if (preview) URL.revokeObjectURL(preview);
      toast.error("Upload failed", { description: "Try again or attach a different file." });
    } finally { setUploading(false); }
  };

  const replaceReceipt = () => {
    // Free the previous object URL so we don't leak memory on repeated
    // replaces. Then clear state and re-open the native picker.
    if (receiptPreview) URL.revokeObjectURL(receiptPreview);
    setReceiptPath("");
    setReceiptPreview("");
    setReceiptName("");
    setReceiptIsImage(false);
    // Reset the input so re-selecting the SAME file still fires onChange.
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // If they have no note AND no screenshot, ask once before submitting.
    if (!note.trim() && !receiptPath) {
      const ok = window.confirm("No note or screenshot added. Submit anyway?");
      if (!ok) return;
    }
    setSubmitting(true); setError("");
    try {
      await api.post(`/payments/${id}/mark-paid`, { method, note, receipt_path: receiptPath || null });
      toast.success("Payment submitted successfully", {
        description: "Your coach will review and confirm your payment shortly.",
      });
      navigate("/parent/dashboard");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (!record) return <AppLayout><p className="text-ink-500">Loading…</p></AppLayout>;
  const accepted = record.charge?.accepted_methods || [];
  const methodList = METHOD_OPTIONS.filter((m) => accepted.includes(m.v) || accepted.length === 0);
  const isRejected = record.status === "rejected";

  return (
    <AppLayout>
      <Link to={back.to} className="inline-flex items-center gap-1 text-sm font-semibold text-ink-500 hover:text-ink-900" data-testid="mark-paid-back">
        <ArrowLeft className="h-4 w-4" /> Back to {back.label}
      </Link>
      <div className="mt-3 max-w-2xl">
        <p className="label-caps">How to Pay</p>
        <h1 className="h2">{record.charge?.title}</h1>
        <p className="text-sm text-ink-500">{record.player?.first_name} {record.player?.last_name} · due {formatDate(record.charge?.due_date)}</p>
        <p className="mt-2 font-heading text-3xl font-bold text-ink-900">{formatCurrency(record.amount)}</p>

        {/* Rejection reason — surfaced prominently for resubmission flow. */}
        {isRejected ? (
          <div
            className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-5"
            data-testid="parent-rejection-banner"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-heading text-base font-bold text-rose-900">
                  Your coach asked you to resubmit this payment.
                </p>
                {record.coach_note ? (
                  <p
                    className="mt-2 whitespace-pre-wrap text-sm text-rose-800"
                    data-testid="parent-rejection-reason"
                  >
                    &ldquo;{record.coach_note}&rdquo;
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-rose-800">
                    Your coach didn&apos;t leave a specific reason — reach out
                    to them if you&apos;re not sure what to change.
                  </p>
                )}
                <p className="mt-3 text-xs text-rose-700">
                  Your previous payment method and note are pre-filled below.
                  Update whatever&apos;s needed and resubmit.
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Coach instructions — front and center so parents know payment happens OFF-app */}
        {record.charge?.payment_instructions ? (
          <div className="mt-4 rounded-2xl border border-ink-100 bg-white p-5 shadow-sm" data-testid="parent-pay-instructions">
            <p className="font-heading text-base font-bold text-ink-900">How to pay your coach</p>
            <p className="mt-2 whitespace-pre-wrap text-sm text-ink-700">{record.charge.payment_instructions}</p>
            <p className="mt-3 text-xs text-ink-500">Pay through your usual method (Venmo, Zelle, Cash App, cash, or check). Squad Fee doesn&apos;t process the payment.</p>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
            <p className="text-sm text-ink-700">Your coach hasn&apos;t added payment instructions for this fee. Ask them how they&apos;d like to be paid (Venmo, Zelle, Cash App, cash, or check).</p>
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-6 space-y-5 rounded-2xl border border-ink-100 bg-white p-6 shadow-sm" data-testid="mark-paid-form">
          <div>
            <h2 className="font-heading text-base font-bold text-ink-900">Already paid?</h2>
            <p className="text-xs text-ink-500">Tell your coach how you paid.</p>
          </div>

          <div>
            <Label>Payment method</Label>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {methodList.map((m) => (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => setMethod(m.v)}
                  className={`rounded-lg border p-3 text-sm font-semibold ${method === m.v ? "border-brand bg-brand-50 text-brand" : "border-ink-200 bg-white text-ink-700 hover:bg-ink-100"}`}
                  data-testid={`pay-method-${m.v}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="note">Add a note (optional)</Label>
            <Textarea id="note" rows={3} placeholder="e.g. Sent this morning, confirmation #ABC123" value={note} onChange={(e) => setNote(e.target.value)} data-testid="pay-note-input" />
          </div>

          <div>
            <Label>Receipt / screenshot</Label>
            <p className="text-xs text-ink-500">Optional, but helpful for faster coach approval.</p>
            {receiptPath ? (
              /* SUCCESS STATE — thumbnail (images) or filename tile (PDFs)
                 plus a green success chip and an explicit Replace button.
                 A hidden file input lives outside so the Replace button can
                 re-open the picker without wrapping the whole card in a
                 <label>. */
              <div
                className="mt-2 flex items-center gap-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4"
                data-testid="pay-upload-success"
              >
                {receiptIsImage && receiptPreview ? (
                  <img
                    src={receiptPreview}
                    alt="Uploaded receipt preview"
                    className="h-16 w-16 shrink-0 rounded-lg border border-emerald-200 object-cover"
                    data-testid="pay-upload-thumbnail"
                  />
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-emerald-200 bg-white">
                    <FileText className="h-7 w-7 text-emerald-600" aria-hidden="true" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 text-sm font-semibold text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    <span>Receipt attached</span>
                  </div>
                  <p
                    className="mt-0.5 truncate text-sm text-ink-900"
                    data-testid="pay-upload-filename"
                    title={receiptName}
                  >
                    {receiptName || "Uploaded file"}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-500">You can replace it before submitting.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={replaceReceipt}
                  disabled={uploading}
                  className="h-10 shrink-0 border-emerald-300 px-3 font-semibold text-emerald-800 hover:bg-emerald-100"
                  data-testid="pay-upload-replace"
                >
                  <RotateCcw className="mr-1 h-4 w-4" /> Replace
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf"
                  onChange={onFile}
                  data-testid="pay-upload-input"
                />
              </div>
            ) : (
              /* EMPTY STATE — same drop area as before. */
              <label
                className="mt-2 flex min-h-[10.5rem] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-brand/40 bg-brand-50/40 p-6 text-center text-sm text-ink-700 hover:border-brand hover:bg-brand-50 focus-within:border-brand focus-within:bg-brand-50"
                data-testid="pay-upload-area"
              >
                {uploading ? (
                  <Loader2 className="h-8 w-8 animate-spin text-brand" aria-hidden="true" />
                ) : (
                  <UploadCloud className="h-10 w-10 text-brand" aria-hidden="true" />
                )}
                <p className="font-semibold text-ink-900">
                  {uploading ? "Uploading…" : "Tap to upload a receipt or screenshot"}
                </p>
                <p className="text-xs text-ink-500">JPG, PNG, or PDF · under 10 MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf"
                  onChange={onFile}
                  data-testid="pay-upload-input"
                />
              </label>
            )}
          </div>

          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p> : null}

          <Button type="submit" disabled={submitting} className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="pay-submit-button">
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
              </span>
            ) : (
              "Submit for coach to confirm"
            )}
          </Button>
          <p className="text-center text-xs text-ink-500">Your coach will review and confirm. You&apos;ll see the final status on your dashboard.</p>
        </form>
      </div>
    </AppLayout>
  );
}
